/**
 * Global admin auth interceptor (Aug 2026 httpOnly cookie migration).
 *
 * Installs an axios response interceptor that transparently refreshes the
 * admin access token on a 401, retries the original request once, and - if
 * refresh fails - clears the cached admin profile and redirects to
 * /admin/login. Tokens live in httpOnly cookies now; the browser attaches
 * them automatically (axios.defaults.withCredentials = true, set in App.js).
 *
 * Call `installAdminAuthInterceptor()` exactly once at app startup.
 *
 * Why global axios and not a dedicated client?
 * Admin API calls are scattered across `src/api/admin.js`, `src/api/bookings.js`
 * and a few admin pages. They all use the default `axios` instance, so one
 * global interceptor covers them without a large refactor.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { AdminStorage } from '../utils/storage';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_URL = `${API}/api/admin/auth/refresh`;

// Pre-auth admin endpoints — a 401 here means "bad credentials", not
// "expired session". Attempting a refresh would just waste a round trip.
const ADMIN_PUBLIC_AUTH_PATHS = [
  '/admin/login', '/admin/verify-mfa', '/admin/resend-mfa',
  '/admin/forgot-password', '/admin/verify-reset-otp',
  '/admin/resend-reset-otp', '/admin/reset-password', '/admin/auth/refresh',
];

// Dedup concurrent refresh attempts so parallel 401s trigger one refresh
let refreshPromise = null;

const redirectToAdminLogin = () => {
  if (!window.location.pathname.startsWith('/admin/login')) {
    toast.error('Your session expired. Please log in again.');
    window.location.href = '/admin/login';
  }
};

const runRefresh = async () => {
  // Body is empty — the admin_refresh_token cookie is read server-side.
  const res = await axios.post(
    REFRESH_URL,
    {},
    { headers: { 'Content-Type': 'application/json' }, _skipAuthRefresh: true }
  );
  return res.data; // { token, refresh_token } — cookies already rotated server-side
};

export const installAdminAuthInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      // Not a response or not a 401 or already retried or explicitly skipped
      if (
        !response ||
        response.status !== 401 ||
        !config ||
        config._retried ||
        config._skipAuthRefresh
      ) {
        return Promise.reject(error);
      }

      // Only admin-scoped calls belong to this interceptor.
      if (!config.url || !config.url.includes('/admin/')) {
        return Promise.reject(error);
      }

      // Pre-auth endpoints — a 401 here is "bad credentials", not
      // "expired session".
      if (ADMIN_PUBLIC_AUTH_PATHS.some((p) => config.url.includes(p))) {
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = runRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;

        // Retry original request — the rotated cookie is already attached.
        config._retried = true;
        return axios(config);
      } catch (refreshErr) {
        AdminStorage.clear();
        redirectToAdminLogin();
        return Promise.reject(error);
      }
    }
  );
};

export default installAdminAuthInterceptor;
